<template>
  <div class="tw:relative" v-click-outside="clickAway" :class="{ 'tw:w-full tw:lg:w-auto': !withShadow }">
    <div
      :class="getClasses"
      class="tw:max-w-full tw:flex tw:items-center tw:justify-between tw:bg-white tw:rounded-lg tw:p-2"
    >
      <div class="tw:py-1 tw:px-2 tw:w-full tw:flex tw:items-center" @click="showPartners">
        <div class="tw:flex tw:flex-col tw:w-full tw:mr-2">
          <p class="tw:text-gray-600 tw:text-xs">{{ $t('connectivity_provider_info') }}</p>
          <p class="tw:text-sm tw:font-bold">{{ selectedPartner?.partnerName }}</p>
        </div>
        <ChevronRight
          v-if="partners.length > 1"
          class="tw:hover:cursor-pointer tw:size-6"
          :class="isOpen && partners.length > 0 && screenWidth > 767 ? 'tw:rotate-270' : 'tw:rotate-90'"
        />
      </div>
      <div class="tw:flex tw:pt-2 tw:lg:pt-0" :class="withShadow ? 'tw:w-full tw:lg:w-auto' : 'tw:w-full tw:md:w-auto'">
        <MainButton
          @click="startCreatingCampaign"
          :class="withShadow ? 'tw:w-full tw:lg:w-auto' : 'tw:w-full tw:md:w-auto'"
          data-testId="start-creating-campaign-button"
        >
          <span class="tw:text-nowrap">{{ isAdmin ? $t('rc_product_page_cta') : $t('rc_admin_log_in_cta') }}</span>
        </MainButton>
      </div>
    </div>
    <div
      v-show="isOpen && partners.length > 1 && screenWidth > 767"
      class="tw:absolute tw:left-0 tw:bg-white shadow tw:min-h-[100px] tw:h-[120px] tw:overflow-scroll tw:w-[65%] tw:top-[68px] tw:rounded-lg"
    >
      <ul class="tw:my-3">
        <li
          v-for="partner in partners"
          :key="partner.partnerId"
          @click="selectPartner(partner)"
          class="tw:py-3 tw:px-[22px] tw:hover:bg-gray-200 tw:flex tw:items-center tw:gap-x-3"
          :class="
            partner.partnerId === selectedPartner.partnerId
              ? 'tw:bg-gray-200 tw:hover:cursor-default'
              : 'tw:hover:cursor-pointer'
          "
        >
          <PartnerIcon />
          <span class="tw:font-bold">{{ partner.partnerName }}</span>
        </li>
      </ul>
    </div>

    <div
      v-if="isOpen && partners.length > 0 && screenWidth < 1024"
      class="tw:fixed tw:inset-0 tw:top-0 tw:z-51 tw:w-full tw:h-full tw:overflow-hidden tw:flex tw:overflow-y-auto tw:z-100"
    >
      <div class="tw:mx-auto tw:w-full tw:max-w-[980px] tw:bg-white tw:m-auto box-shadow tw:relative">
        <div
          class="tw:fixed tw:z-50 tw:w-full tw:h-[72px] tw:bg-white tw:flex tw:flex-row tw:justify-between tw:items-center tw:py-3.5 tw:px-8 tw:text-gray-800"
        >
          <div>
            <button class="tw:outline-hidden tw:pr-2 tw:pt-2" @click="clickAway" @keydown.enter="clickAway">
              <Close class="tw:size-2.5" />
            </button>
          </div>
          <div class="tw:font-bold">{{ $t('rc_opportunities_commission_cta') }}</div>
          <div />
        </div>

        <div class="tw:flex tw:flex-col tw:pb-[76px] tw:pt-[72px] tw:min-h-screen tw:p-5">
          <p class="tw:font-bold">{{ $t('connectivity_provider_info') }}</p>
          <ul class="tw:my-3">
            <li
              v-for="partner in partners"
              :key="partner.partnerId"
              @click="selectPartner(partner)"
              class="tw:py-3 tw:px-[22px] tw:hover:bg-gray-200 tw:flex tw:items-center tw:gap-x-3"
              :class="
                partner.partnerId === selectedPartner.partnerId
                  ? 'tw:bg-gray-200 tw:hover:cursor-default'
                  : 'tw:hover:cursor-pointer'
              "
            >
              <PartnerIcon />
              <span class="tw:font-bold">{{ partner.partnerName }}</span>
            </li>
          </ul>
        </div>

        <div
          class="footer shadow tw:fixed tw:bottom-0 tw:w-full tw:flex tw:flex-row tw:justify-between tw:items-center tw:py-4 tw:px-8 tw:text-gray-800 tw:bg-white"
        >
          <div class="tw:md:flex tw:lg:hidden tw:w-full">
            <MainButton class="tw:w-full" @click="startCreatingCampaign" :disabled="!selectedPartner">
              <span class="tw:text-nowrap">{{ isAdmin ? $t('rc_product_page_cta') : $t('rc_admin_log_in_cta') }}</span>
            </MainButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'

import MainButton from '@/components/base/buttons/MainButton.vue'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import PartnerIcon from '@/components/icons/PartnerIcon.svg?component'
import Close from '@/components/icons/Close.svg?component'

import { useIsMobile } from '@/composables/useIsMobile'

const isOpen = ref(false)
const props = defineProps({
  partners: Array,
  selectedPartner: Object,
  withShadow: Boolean,
  isAdmin: Boolean,
})

const { screenWidth } = useIsMobile()
const emit = defineEmits(['selectPartner', 'startCreatingCampaign'])
const selectPartner = partner => {
  emit('selectPartner', partner)
  if (screenWidth.value > 767) {
    isOpen.value = false
  }
}

const startCreatingCampaign = () => {
  emit('startCreatingCampaign')
}

const showPartners = () => {
  isOpen.value = !isOpen.value
}
const clickAway = () => {
  isOpen.value = false
}

const getClasses = computed(() => {
  const classes = []
  if (props.withShadow) {
    classes.push('shadow tw:mr-0 tw:md:mr-6 tw:lg:mr-0 tw:flex-col tw:lg:flex-row tw:lg:max-w-[590px] tw:mb-12')
  } else {
    classes.push('tw:md:flex-row tw:flex-col')
  }
  if (props.partners.length > 1) {
    classes.push('tw:hover:cursor-pointer')
  }

  return classes.join(' ')
})
</script>
